// src/pages/AdminPage.tsx
import React, { useState, useEffect } from 'react';
import { View, Heading, Text, Loader, Card, Flex, Divider } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AdminTools from '../components/AdminTools';
import { useNavigate } from 'react-router-dom';
import { Shield, Settings } from 'lucide-react';

const AdminPage: React.FC = () => {
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAdminStatus = async () => {
            try {
                setLoading(true);
                const session = await fetchAuthSession();
                
                // Get groups from the token
                const payload = session.tokens?.accessToken?.payload || {};
                const groups = Array.isArray(payload['cognito:groups']) 
                    ? payload['cognito:groups'] 
                    : [];
                
                const userIsAdmin = groups.includes('Admin');
                setIsAdmin(userIsAdmin);

                // Redirect non-admin users
                if (!userIsAdmin) {
                    navigate('/');
                }
            } catch (error) {
                console.error('Error checking admin status:', error);
                setIsAdmin(false);
                navigate('/');
            } finally {
                setLoading(false);
            }
        };

        checkAdminStatus();
    }, [navigate]);

    if (loading) {
        return (
            <View padding="2rem" textAlign="center">
                <Loader size="large" />
                <Text marginTop="1rem">Verifying admin permissions...</Text>
            </View>
        );
    }

    if (!isAdmin) {
        return null; // This will be redirected, but just in case
    }

    return (
        <View padding="2rem">
            <Flex direction="column" alignItems="flex-start">
                <Flex alignItems="center" gap="1rem" marginBottom="1.5rem">
                    <View 
                        width="3rem" 
                        height="3rem" 
                        backgroundColor="rgba(59, 130, 246, 0.2)"
                        borderRadius="50%"
                        display="flex"
                    >
                        <Shield size={24} color="#3B82F6" />
                    </View>
                    <Heading level={1}>Admin Dashboard</Heading>
                </Flex>

                <Text color="#AAAAAA" marginBottom="2rem">
                    This dashboard provides administrative tools for managing the Banjara application.
                    Use these tools with caution as they can make system-wide changes.
                </Text>

                {/* Admin Tools Component */}
                <AdminTools />

                {/* Additional Admin Information Cards */}
                <Flex 
                    direction={{ base: 'column', large: 'row' }} 
                    gap="1.5rem" 
                    marginTop="2rem"
                    width="100%"
                >
                    <Card 
                        backgroundColor="#1A1A1A" 
                        padding="1.5rem"
                        flex="1"
                    >
                        <Flex alignItems="center" gap="0.5rem" marginBottom="1rem">
                            <Settings size={20} color="#3B82F6" />
                            <Heading level={4}>Admin Instructions</Heading>
                        </Flex>
                        <Divider marginBottom="1rem" />
                        <Text color="#AAAAAA">
                            <p><strong>Storage Tier Management:</strong> Initialize the storage tier configurations for new users.</p>
                            <p><strong>Trip Management:</strong> Delete trips and their associated photos. Use this when users report issues with specific trips or for content moderation.</p>
                        </Text>
                    </Card>
                    
                    <Card 
                        backgroundColor="#1A1A1A" 
                        padding="1.5rem"
                        flex="1"
                    >
                        <Flex alignItems="center" gap="0.5rem" marginBottom="1rem">
                            <Shield size={20} color="#F59E0B" />
                            <Heading level={4}>Admin Security Notice</Heading>
                        </Flex>
                        <Divider marginBottom="1rem" />
                        <Text color="#AAAAAA">
                            <p><strong>Caution:</strong> Admin actions are permanent and cannot be undone.</p>
                            <p><strong>Security:</strong> Always verify trip IDs before deletion to ensure the correct content is being removed.</p>
                            <p><strong>Logging:</strong> All admin actions are logged for security and audit purposes.</p>
                        </Text>
                    </Card>
                </Flex>
            </Flex>
        </View>
    );
};

export default AdminPage;